import React, {memo, useState} from 'react';

import {
  SafeAreaView,
  View,
  useWindowDimensions,
  Platform,
} from 'react-native';

import DragList, {
  DragListRenderItemInfo,
} from 'react-native-draglist';

import ContentItem from './items/contentitem';
import {useFolder} from '../../hooks/useFolder';
import {
  ActivityIndicator,
  IconButton,
  List,
} from 'react-native-paper';
import {useIsFocused, useRoute} from '@react-navigation/native';
import {FolderPreviewContentType, FolderType} from '../../types';
import {getById, getFolderContent, writeItem} from '../../api';
import {FolderParams} from '../../types/nav';
import {FolderNavigator, RenderContentType} from './foldernavigator';
import {styles} from './styles';
import {AttachmentDrawer} from '../contacts/components/attachmentdrawer';
import {useFileSelectOptions} from '../../hooks/useFileSelectOptions';
import {setEditingMode} from '../../store/slices/ui';
import {useAppDispatch, useAppSelector} from '../../store/hooks';

export interface FolderLibraryProps {
  folderId?: string;
  parentFolderId?: string;
  mnemonic?: string;
  folderType: FolderType;
}
const FolderPage = (props: FolderLibraryProps) => {
  const {folderType} = props;
  const dispatch = useAppDispatch();
  const isFocused = useIsFocused();
  const {height, width} = useWindowDimensions();
  const isMobile = width > 700 ? false : true;
  const route = useRoute();
  let marginTop = Platform.OS === 'web' ? 0 : 0;
  const params = route.params as FolderParams;
  let {mnemonic, title, userId} = params || {};
  if (props.mnemonic) mnemonic = props.mnemonic;
  const folderId = props?.folderId || params?.folderId;
  const dataHook = useFolder(
    folderId,
    getFolderContent,
    getById,
    folderType,
    writeItem,
    mnemonic,
    title,
    userId,
  );
  const {
    folderContent,
    isLoadingFolder,
    folderContentQuery,
    folder,
    onReordered,
  } = dataHook;

  let parentFolderId =
    props?.parentFolderId ||
    params?.parentFolderId ||
    (folder && folder.folderId) ||
    null;
  if (parentFolderId === folderId) parentFolderId = null;
  const parentFolderHook = useFolder(
    parentFolderId,
    getFolderContent,
    getById,
    folderType,
    writeItem,
    mnemonic,
  );

  const mstyles = styles;
  mstyles.item.width = width - 80;
  mstyles.scroll.height = height - 65;
  mstyles.scroll.marginTop = marginTop;
  const fileSelectOptions = useFileSelectOptions(folderId);
  const hasMedia = useAppSelector(
    state => state.media.currentMediaId !== undefined,
  );
  const [scroll, setScroll] = useState(0);
  if (!isFocused) return <></>;
  const renderContent =
    (width: number, dataHook: ReturnType<typeof useFolder>) =>
    ({
      item: i,
      onDragEnd,
      onDragStart,
    }: DragListRenderItemInfo<FolderPreviewContentType>) => {
      const tstyles = {...mstyles};
      tstyles.item.width = width;
      return (
        <ContentItem
          i={i}
          key={i.id}
          dataHook={dataHook}
          styles={tstyles}
          width={width}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        />
      );
    };
  // const isMobile = width < 700
  const dynamicHeight = hasMedia
    ? isMobile
      ? height - height / 4
      : height / 2
    : height - (isMobile ? (Platform.OS === 'ios' ? 220 : 140) : 0);
  return (
    <SafeAreaView>
      <View
        style={{
          height: dynamicHeight,
          width: '100%',
          flexDirection: 'row',
        }}>
        {!isMobile ? (
          <FolderNavigator
            parentFolderId={parentFolderId}
            parentFolderHook={parentFolderHook}
            renderContent={
              renderContent(
                !isMobile ? (width / 100) * 33 : width,
                parentFolderHook,
              ) as RenderContentType
            }
            folderType={folderType}
          />
        ) : null}
        {!isLoadingFolder &&
        !folderContentQuery.isFetching &&
        folderContent !== undefined ? (
          <DragList
            renderItem={renderContent(
              !isMobile ? (width / 100) * 70 : width,
              dataHook,
            )}
            keyExtractor={item => item?.id || item?.date.toString()}
            style={{
              width: '100%',
              height: dynamicHeight,
              paddingBottom: 50,
            }}
            data={folderContent}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            scrollEnabled={true}
            onScroll={e => {
              setScroll(e.nativeEvent.contentOffset.x);
            }}
            ref={e => {
              e?.scrollToOffset({offset: scroll, animated: false});
            }}
            ListEmptyComponent={() => (
              <List.Item
                title={'Create your first project'}
                left={() => (
                  <IconButton icon={'creation'} mode="outlined" />
                )}
                style={{padding: 10}}
                onPress={() => {
                  if (folderType === FolderType.contact) {
                    dispatch(setEditingMode('party'));
                    return;
                  }
                  fileSelectOptions.showDrawer();
                }}
              />
            )}
            onReordered={onReordered}
          />
        ) : (
          <ActivityIndicator
            style={{
              position: 'absolute',
              top: height / 3,
              left: 0,
              right: 0,
            }}
            size={72}
          />
        )}
        <AttachmentDrawer {...fileSelectOptions} />
      </View>
    </SafeAreaView>
  );
};

export default memo(FolderPage);
